import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import TrainingModeSelector from './TrainingModeSelector';
import { DEFAULT_TIER, TIER_INFO } from '../lib/learningTiers';
import {
  CUSTOM_REVIEW_MAX_SELECTIONS,
  customReviewCandidateKey,
  filterReviewCandidates,
  groupReviewCandidatesByDate,
  normalizeReviewCandidates,
  selectReviewCandidates,
  selectedReviewCandidates,
  toggleReviewCandidateKey,
} from '../lib/customReview';
import { normalizeTrainingMode } from '../lib/trainingModes';

const CandidateGroupCheckbox = ({ checked, indeterminate, onChange, label }) => {
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return (
    <input
      ref={inputRef}
      type="checkbox"
      checked={checked}
      aria-label={label}
      aria-checked={indeterminate ? 'mixed' : checked}
      onChange={onChange}
      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
    />
  );
};

const CustomReviewSelector = ({
  open = false,
  onClose,
  onStart,
  availableTiers = [],
  initialTier = DEFAULT_TIER,
  initialTrainingMode = 'meaning',
  modal = true,
}) => {
  const { t, apiMessage } = useI18n();
  const [trainingMode, setTrainingMode] = useState(normalizeTrainingMode(initialTrainingMode));
  const [tier, setTier] = useState(initialTier || DEFAULT_TIER);
  const [items, setItems] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [collapsedDates, setCollapsedDates] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    if (!open) return;
    setTrainingMode(normalizeTrainingMode(initialTrainingMode));
    setTier(initialTier || DEFAULT_TIER);
  }, [initialTier, initialTrainingMode, open]);

  const tierOptions = useMemo(() => {
    const values = Array.isArray(availableTiers) && availableTiers.length
      ? availableTiers
      : [initialTier || DEFAULT_TIER];
    return [...new Set(values.filter(Boolean))];
  }, [availableTiers, initialTier]);

  useEffect(() => {
    if (!tierOptions.includes(tier)) setTier(tierOptions[0] || DEFAULT_TIER);
  }, [tier, tierOptions]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const controller = new AbortController();
    const loadCandidates = async () => {
      setLoading(true);
      setError('');
      setNotice('');
      setItems([]);
      setSelectedKeys(new Set());
      setQuery('');
      setCollapsedDates(new Set());
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ training_mode: trainingMode });
        const response = await fetch(`/api/student/review-candidates?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(apiMessage(payload, t('Custom review candidates could not be loaded.')));
        }
        const candidates = normalizeReviewCandidates(payload?.items, trainingMode);
        if (active) setItems(candidates);
      } catch (loadError) {
        if (loadError?.name !== 'AbortError' && active) {
          setError(loadError.message || t('Custom review candidates could not be loaded.'));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadCandidates();
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiMessage, open, reloadNonce, t, trainingMode]);

  const filteredItems = useMemo(() => filterReviewCandidates(items, query), [items, query]);
  const groups = useMemo(() => groupReviewCandidatesByDate(filteredItems), [filteredItems]);
  const visibleKeys = useMemo(
    () => filteredItems.map(customReviewCandidateKey),
    [filteredItems],
  );
  const selectedCount = selectedKeys.size;
  const tierInfo = TIER_INFO[tier] || TIER_INFO[DEFAULT_TIER];

  const showMaxNotice = () => {
    setNotice(t('You can select up to {count} meanings at a time.', { count: CUSTOM_REVIEW_MAX_SELECTIONS }));
  };

  const toggleOne = (item) => {
    const result = toggleReviewCandidateKey(selectedKeys, customReviewCandidateKey(item));
    if (result.blocked) showMaxNotice();
    setSelectedKeys(result.selectedKeys);
  };

  const toggleGroup = (group) => {
    const groupKeys = group.items.map(customReviewCandidateKey);
    const allSelected = groupKeys.length > 0 && groupKeys.every((key) => selectedKeys.has(key));
    if (allSelected) {
      const next = new Set(selectedKeys);
      groupKeys.forEach((key) => next.delete(key));
      setSelectedKeys(next);
      return;
    }
    const result = selectReviewCandidates(selectedKeys, group.items);
    if (result.blocked) showMaxNotice();
    setSelectedKeys(result.selectedKeys);
  };

  const selectVisible = () => {
    const result = selectReviewCandidates(selectedKeys, filteredItems);
    if (result.blocked) showMaxNotice();
    setSelectedKeys(result.selectedKeys);
  };

  const clearVisible = () => {
    const visibleSet = new Set(visibleKeys);
    setSelectedKeys((current) => new Set([...current].filter((key) => !visibleSet.has(key))));
  };

  const handleStart = () => {
    const selection = selectedReviewCandidates(items, selectedKeys);
    if (!selection.length) {
      setNotice(t('Select at least one meaning to start custom review.'));
      return;
    }
    onStart?.({ items: selection, trainingMode, tier });
  };

  if (!open) return null;

  const content = (
    <div
      role={modal ? 'dialog' : undefined}
      aria-modal={modal ? 'true' : undefined}
      aria-labelledby="custom-review-title"
      data-testid="custom-review-selector"
      className={`${modal ? 'max-h-[calc(100vh-2rem)] overflow-y-auto' : ''} w-full max-w-5xl rounded-xl bg-white p-4 shadow-2xl sm:p-6`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="custom-review-title" className="text-xl font-bold text-gray-900 sm:text-2xl">
            {t('Custom review')}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {t('Choose learned meanings by date, then start the same review exercises.')}
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Close custom review')}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
        <TrainingModeSelector
          value={trainingMode}
          onChange={(next) => {
            setTrainingMode(normalizeTrainingMode(next));
            setNotice('');
          }}
          legend="Review mode"
          name="custom-review-mode"
          className="min-w-0"
        />
        <div>
          <label htmlFor="custom-review-tier" className="block text-sm font-semibold text-gray-900">
            {t('Choose difficulty')}
          </label>
          <select
            id="custom-review-tier"
            value={tier}
            onChange={(event) => setTier(event.target.value)}
            className="mt-2 w-full rounded-md border border-gray-300 bg-white p-3 text-sm"
          >
            {tierOptions.map((option) => (
              <option key={option} value={option}>{t(TIER_INFO[option]?.studentLabel || option)}</option>
            ))}
          </select>
          <p className="mt-2 text-xs text-gray-500">{t(tierInfo?.studentDescription)}</p>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label htmlFor="custom-review-search" className="sr-only">{t('Search learned meanings')}</label>
        <div className="relative min-w-0 flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            id="custom-review-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('Search by English word or Chinese meaning')}
            className="w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <button type="button" onClick={selectVisible} disabled={loading || !filteredItems.length} className="rounded-md border border-indigo-200 px-3 py-2 font-medium text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50">
            {t('Select visible')}
          </button>
          <button type="button" onClick={clearVisible} disabled={loading || !filteredItems.length} className="rounded-md border border-gray-300 px-3 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
            {t('Clear visible')}
          </button>
          <span className="rounded-md bg-gray-100 px-3 py-2 font-semibold tabular-nums text-gray-700" aria-live="polite">
            {t('{count} selected', { count: selectedCount })}
          </span>
        </div>
      </div>
      <p className="mt-2 text-xs text-gray-500">{t('Maximum {count} meanings per custom review.', { count: CUSTOM_REVIEW_MAX_SELECTIONS })}</p>

      {notice && <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">{notice}</p>}
      {error && (
        <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => setReloadNonce((value) => value + 1)} className="mt-3 inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 font-semibold text-rose-700 shadow-sm hover:bg-rose-100">
            <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
            {t('Retry')}
          </button>
        </div>
      )}

      <div className="mt-4 min-h-[10rem] rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
        {loading && <p className="py-10 text-center text-sm text-gray-500">{t('Loading learned meanings...')}</p>}
        {!loading && !error && items.length === 0 && (
          <p className="py-10 text-center text-sm text-gray-600">{t('No learned meanings are available for custom review.')}</p>
        )}
        {!loading && !error && items.length > 0 && groups.length === 0 && (
          <p className="py-10 text-center text-sm text-gray-600">{t('No results match your search.')}</p>
        )}
        {!loading && !error && groups.length > 0 && (
          <div className="space-y-3">
            {groups.map((group) => {
              const groupKeys = group.items.map(customReviewCandidateKey);
              const selectedInGroup = groupKeys.filter((key) => selectedKeys.has(key)).length;
              const allSelected = groupKeys.length > 0 && selectedInGroup === groupKeys.length;
              const collapsed = collapsedDates.has(group.date || '__unknown__');
              const groupLabel = group.date
                ? t('Learned {date} · {count} meanings', { date: group.date, count: group.count })
                : t('Unknown learning date · {count} meanings', { count: group.count });
              return (
                <section key={group.date || '__unknown__'} className="rounded-lg border border-gray-200 bg-white">
                  <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-3 py-2.5 sm:px-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <CandidateGroupCheckbox
                        checked={allSelected}
                        indeterminate={selectedInGroup > 0 && !allSelected}
                        onChange={() => toggleGroup(group)}
                        label={t('Select all meanings learned {date}', { date: group.date || t('on an unknown date') })}
                      />
                      <button
                        type="button"
                        onClick={() => setCollapsedDates((current) => {
                          const next = new Set(current);
                          const key = group.date || '__unknown__';
                          if (next.has(key)) next.delete(key); else next.add(key);
                          return next;
                        })}
                        aria-expanded={!collapsed}
                        className="min-w-0 truncate text-left text-sm font-semibold text-gray-900 hover:text-indigo-700"
                      >
                        {groupLabel}
                      </button>
                    </div>
                    <span className="text-xs tabular-nums text-gray-500">{selectedInGroup}/{group.count}</span>
                  </div>
                  {!collapsed && (
                    <div className="divide-y divide-gray-100">
                      {group.items.map((item) => {
                        const key = customReviewCandidateKey(item);
                        const selected = selectedKeys.has(key);
                        return (
                          <label key={key} className="flex cursor-pointer items-start gap-3 px-3 py-3 hover:bg-indigo-50/50 sm:px-4">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleOne(item)}
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-2">
                                <span data-i18n-skip className="font-semibold text-gray-900">{item.word}</span>
                                <span data-i18n-skip className="text-sm text-gray-700">{item.definition_cn || item.definition_en || t('Meaning unavailable')}</span>
                                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${item.due ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}>
                                  {item.due ? t('Due') : t('Extra practice')}
                                </span>
                              </span>
                              <span className="mt-1 block text-xs text-gray-500">
                                {item.definition_en && item.definition_cn && <span data-i18n-skip>{item.definition_en}</span>}
                                {item.next_review_date && <span className="ml-2">{t('Next review {date}', { date: item.next_review_date })}</span>}
                                {item.sense_id !== 'primary' && <span className="ml-2 text-gray-400">{t('Additional meaning')}</span>}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-3 text-xs leading-5 text-indigo-900">
        {t('Due meanings count toward today’s review. Extra practice meanings do not change their original review schedule.')}
      </div>
      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onClose && <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-50">{t('Cancel')}</button>}
        <button
          type="button"
          onClick={handleStart}
          disabled={loading || Boolean(error) || selectedCount === 0}
          className="rounded-md bg-indigo-600 px-5 py-2.5 font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {t('Start custom review')}
        </button>
      </div>
    </div>
  );

  return modal ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
      {content}
    </div>
  ) : content;
};

export default CustomReviewSelector;
